export interface NavFitInput {
  // Width of each navigation item, in the order they appear.
  itemWidths: number[];
  // Width of the overflow trigger, which only exists when something is folded.
  triggerWidth: number;
  // Gap between items in the bar.
  gap: number;
  // Room available, already reduced by the breathing space to keep clear.
  budget: number;
}

// How many leading items fit in the given budget. The whole list needs no
// overflow trigger, so it is measured without one; any shorter list has to
// leave room for the trigger, which is why the two cases are measured
// separately rather than always reserving the trigger's width.
export function computeVisibleCount({
  itemWidths,
  triggerWidth,
  gap,
  budget
}: NavFitInput): number {
  const total = itemWidths.length;
  const sumOf = (count: number): number =>
    itemWidths.slice(0, count).reduce((sum, width) => sum + width, 0);

  const widthForAll = sumOf(total) + gap * Math.max(0, total - 1);
  if (widthForAll <= budget) return total;

  // The trigger sits after the visible items, so a bar showing `count` items
  // has `count` gaps: one between each pair, plus one before the trigger.
  let fitting = total - 1;
  while (fitting > 0 && sumOf(fitting) + triggerWidth + gap * fitting > budget) {
    fitting--;
  }
  return fitting;
}
